/**
 * Whether a time of day rule is on at a given moment, worked out from its own settings: the hours
 * for each day, holidays, what the hours mean, manual mode, and the rule's time zone.
 *
 * The phone system's list of rules also carries a `state`, but what it holds for a rule that
 * switches by the clock isn't confirmed on 69 (on one production rule it said "on" in the middle
 * of open hours), so Stitch reads the rule's settings instead, the same way the phone system does.
 */
import type { FieldValues } from './catalog.js';
import { parseHours, type DayField } from './describe.js';
import { parseHolidays, type Holiday } from './holidays.js';

export interface LocalTime {
  /** YYYY-MM-DD */
  date: string;
  day: DayField;
  /** Minutes since midnight. */
  minutes: number;
}

const WEEKDAY: Record<string, DayField> = { Mon: 'mon', Tue: 'tue', Wed: 'wed', Thu: 'thu', Fri: 'fri', Sat: 'sat', Sun: 'sun' };

/** The date, weekday and time of day at a moment, in a time zone. */
export function localTime(tz: string, at: Date): LocalTime {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(at)
      .map((p) => [p.type, p.value])
  ) as Record<string, string>;
  return { date: `${parts.year}-${parts.month}-${parts.day}`, day: WEEKDAY[parts.weekday ?? 'Mon'] ?? 'mon', minutes: Number(parts.hour) * 60 + Number(parts.minute) };
}

/** A holiday falls on this date (YYYY-MM-DD); yearly ones ("MM-DD") on any year. */
export function holidayCovers(h: Holiday, date: string): boolean {
  const to = h.to || h.from;
  if (/^\d{2}-\d{2}$/.test(h.from)) return h.from <= date.slice(5) && date.slice(5) <= to;
  return h.from <= date && date <= to;
}

/** Whether a time of day rule is on at a local time, from its hours and holidays. */
export function ruleOnAt(values: FieldValues, holidays: Holiday[], t: LocalTime): boolean {
  if (values.mode === 'manual') return values.manualState === 'on';
  const meaningOn = values.hoursMeaning === 'on';
  const holiday = holidays.find((h) => holidayCovers(h, t.date));
  const hours = holiday ? (parseHours(holiday.hours) ?? []) : (parseHours(values[t.day]) ?? []);
  const inHours = hours.some((i) => i.from <= t.minutes && t.minutes < i.to);
  return inHours ? meaningOn : !meaningOn;
}

/** The phone system's short time zone codes, as IANA zones. */
const PBX_ZONES: Readonly<Record<string, string>> = {
  ET: 'America/New_York',
  EST: 'America/New_York',
  EDT: 'America/New_York',
  CT: 'America/Chicago',
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  MT: 'America/Denver',
  MST: 'America/Denver',
  MDT: 'America/Denver',
  AZ: 'America/Phoenix',
  PT: 'America/Los_Angeles',
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  AKT: 'America/Anchorage',
  HT: 'Pacific/Honolulu',
  HST: 'Pacific/Honolulu'
};

function validZone(z: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: z });
    return true;
  } catch {
    return false;
  }
}

/** The zone a rule's hours are read in: its own, when it has one Stitch recognises; else the company's. */
export function ruleZone(values: FieldValues, companyZone: string): string {
  const own = (values.timezone ?? '').trim();
  if (!own) return companyZone;
  const mapped = PBX_ZONES[own.toUpperCase()];
  if (mapped) return mapped;
  return own.includes('/') && validZone(own) ? own : companyZone;
}

/** Whether a time of day rule is on right now. */
export function ruleOnNow(values: FieldValues, companyZone: string, now: Date = new Date()): boolean {
  return ruleOnAt(values, parseHolidays(values.holidays) ?? [], localTime(ruleZone(values, companyZone), now));
}
